import { useEffect } from "react";

import { useFeatureToggle } from "@bokio/hooks/useFeatureToggle/useFeatureToggle";
import { useScript } from "@bokio/hooks/useScript/useScript";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import { usePricePlanContext } from "@bokio/mobile-web-shared/core/contexts/PricePlanContext/PricePlanContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useUser } from "@bokio/shared/state/requests";

interface SaltfishProps {
	enableAnalytics: boolean;
}

interface SaltfishPlayerProps {
	playlistId: SaltfishPlaylists;
	allowMultiplePlays?: boolean;
}

export enum SaltfishPlaylists {
	WelcomeMessageCarouselChecklist = "e022bddf-eedf-4633-8b26-0ab398a6ba4d",
	WelcomeMessageVerticalChecklist = "e94566a7-80bf-4484-b001-891d33ab84eb",
	Invoices = "b0f0d795-a46c-4911-81e3-bc1f5707c99c",
	Uploads = "fe6008ec-e732-443d-8183-8f06d747c4d1",
	ETRNoBankConnection = "d89670c6-e01b-4a2b-ba66-c0f80b50e945",
	ETRWithBankConnection = "397c2f41-b41c-41fb-8832-1fc04de78c9b",

	BasicPricing = "dd383886-a8d1-4844-ac01-632e4306ee2b",
	PremiumPricing = "0396a5bb-2269-4d79-ab49-334788e96056",
	BusinessPricing = "3119010c-5d83-41b9-a8b6-f8539d313fbb",
	PlusPricing = "8b51d3b7-9525-4a31-9817-6d1137442e69",
}

const obfuscateUserId = (userId: string) => {
	// TF 2025-07-08 Just don't send exact userid to saltfish, obfuscation is enough as it is not that sensitive
	return userId.replaceAll("-", "").slice(1) + userId.charAt(0);
};

// Exported utility for use in other components
export function isSaltFishEnabledFn({
	inTrial,
	companyInfo,
}: {
	inTrial: boolean;
	companyInfo: m.Settings.Controllers.CompanyInfoForView;
}): boolean {
	return (
		(inTrial || companyInfo.Plan === m.Entities.BokioPlan.Free) &&
		companyInfo.Country === m.CountryCode.SE &&
		companyInfo.CompanySystem === m.Entities.CompanySystem.Default
	);
}

export const SaltfishPlayer = (props: SaltfishPlayerProps) => {
	const { isFeatureEnabled } = useFeatureToggle();
	const isUnderDevelopmentEnabled = isFeatureEnabled(m.Core.Features.UnderDevelopment);
	const { companyInfo } = useCompanyInfo();
	const { inTrial } = usePricePlanContext();
	const getUser = useUser();

	// Usage inside SaltfishPlayer
	const isSaltFishEnabled = isSaltFishEnabledFn({ inTrial, companyInfo });

	const saltfishWidget = window.saltfish;
	useEffect(() => {
		if (!!saltfishWidget && getUser.user?.Id && isSaltFishEnabled) {
			saltfishWidget.identify(obfuscateUserId(getUser.user?.Id), ""); // Identify user with saltfish, required to use the "once" option when playing playlist
			saltfishWidget.startPlaylist(props.playlistId, {
				once: !isUnderDevelopmentEnabled && !!!props.allowMultiplePlays,
			}); // Only play this playlist once for each user, except explicitly set to play multiple times, or if the FeatureUnderDevelopment flag is set, we want to re-view videos then
		}
	}, [
		saltfishWidget,
		props.playlistId,
		getUser.user?.Id,
		isSaltFishEnabled,
		isUnderDevelopmentEnabled,
		props.allowMultiplePlays,
	]);
	return null;
};

export const Saltfish = (props: SaltfishProps) => {
	const scriptRequest = useScript("scripts/saltfish.js");
	const isScriptLoaded = !!scriptRequest.data;
	useEffect(() => {
		if (isScriptLoaded) {
			const saltfishWidget = window.saltfish;
			saltfishWidget.init({
				token: "IxcEnaYqho78sbr9YeON",
				enableAnalytics: props.enableAnalytics,
				persistence: true, // Remember user progress between visits. Default: false
				sessionRecording: false, // Enable detailed session recording. Default: false
			});
		}
	}, [isScriptLoaded, props.enableAnalytics]);

	return null;
};
